import { useForm, Controller, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal as ManModal,
  Box as ManBox,
  TextInput as MuiTextInput,
  Group as ManGroup,
  Button as ManButton,
  Select as ManSelect,
} from "@mantine/core"
import { useEffect, useRef, useState } from "react"

import ErrorText from "../../components/ErrorText"
import {
  axiosInstance,
  axiosInstanceNoAuth,
  axiosReq,
} from "../../api/axiosDefaults"
import { formSchema, isCallnameHiddenRole } from "./addUserFormValidation"
import { toast } from "react-hot-toast"
import axios from "axios"
import { CLIENT_PROGRAM_HOSTNAME } from "../../utils/config"
import useHostName from "../../hooks/useHostName"
import callnames from "../../data/callnames"

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  fallback

const EditUserModal = (initialData) => {
  const host = useHostName()
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(formSchema),
  })

  const [allGroups, setAllGroups] = useState([])
  const [characters, setCharacters] = useState([])

  const selectedGroup = useWatch({
    control,
    name: "groups",
  })

  const selectedCharacter = useWatch({
    control,
    name: "character",
  })
  const prevSelectedGroupRef = useRef()

  const selectedRoleName = allGroups.find(
    (group) => String(group.value) === String(selectedGroup)
  )?.label
  const isCastRole = (selectedRoleName || "").toLowerCase() === "cast"
  const isCrewRole = (selectedRoleName || "").toLowerCase() === "crew"
  const hideCallTimeUserName = isCallnameHiddenRole(selectedRoleName)
  const characterOptions = [
    { label: "\u00A0", value: "" },
    ...characters.map(({ label, value }) => ({ label, value })),
  ]
  const callnameOptions = [
    { label: "\u00A0", value: "" },
    ...callnames.map((name) => ({ label: name, value: name })),
  ]

  // Mirrored into form state so the schema can make the callname conditional.
  useEffect(() => {
    setValue("roleName", selectedRoleName || "", {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [selectedRoleName, setValue])

  useEffect(() => {
    if (prevSelectedGroupRef.current === undefined) {
      prevSelectedGroupRef.current = selectedGroup
      return
    }

    if (prevSelectedGroupRef.current === selectedGroup) return

    prevSelectedGroupRef.current = selectedGroup
    setValue("character", null, {
      shouldDirty: false,
      shouldValidate: false,
    })
    setValue("callTimeUserName", "", {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [selectedGroup, setValue])

  useEffect(() => {
    const user = initialData?.initialData?.original
    if (!user) return

    setValue("email", user?.email || "")
    setValue("username", user?.username || "")
    setValue("firstName", user?.first_name || "")
    setValue("lastName", user?.last_name || "")
    setValue("callTimeUserName", user?.call_time_username || "")
    setValue("phoneNumber", user?.phone_number || "")
    setValue("groups", user?.groups?.[0]?.id || null)
    setValue("character", null)
  }, [initialData, setValue])

  useEffect(() => {
    if (initialData?.userEditFormOpened) return

    reset()
    prevSelectedGroupRef.current = undefined
  }, [initialData?.userEditFormOpened, reset])
  // useEffect(() => {
  //   axiosInstanceNoAuth
  //     .get("/groups/")
  //     .then((res) => {
  //       setTimeout(() => {
  //         let groupResults = res.data.results;
  //         // If user is Admin, filter out Superadmin
  //         if (initialData?.userType === "Admin") {
  //           groupResults = groupResults.filter((group) => group.name !== "Superadmin");
  //         }
  //         console.log(initialData?.userType)
  //         console.log(groupResults)
  //         const updatedGrpArray = groupResults.map((r) => ({
  //           label: r.name,
  //           value: r.id,
  //         }));
  //         setAllGroups(updatedGrpArray);
  //       },3000)
  //     })
  //     .catch((err) => {
  //       console.log(err)
  //     })
  // }, [])
  useEffect(() => {
    if (!initialData?.userType) return

    axiosInstanceNoAuth
      .get("/groups/")
      .then((res) => {
        let groupResults = res.data.results

        if (initialData?.userType === "Admin") {
          groupResults = groupResults.filter(
            (group) => group.name !== "Superadmin"
          )
        }

        const updatedGrpArray = groupResults.map((r) => ({
          label: r.name,
          value: r.id,
        }))

        setAllGroups(updatedGrpArray)
      })
      .catch((err) => {
        console.error("Error fetching groups:", err)
      })
  }, [initialData])

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        let res
        if (host === CLIENT_PROGRAM_HOSTNAME) {
          res = await axiosReq.get("/characters/")
        } else {
          res = await axiosInstance.get(
            `${localStorage.getItem("projectSlug")}/characters/`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              },
              withCredentials: true,
            }
          )
        }

        const result = res?.data ?? res
        if (result?.success === false || result?.status === false) {
          throw result?.error || new Error("Failed to fetch characters")
        }

        const updatedCharacterArray = (res.data?.results || []).map(
          (character) => ({
            label: character.role,
            value: character.id,
            callTimeUserName: character.username || "",
          })
        )
        setCharacters(updatedCharacterArray)
      } catch (err) {
        console.error("Error fetching characters:", err)
        toast.error(getErrorMessage(err, "Error fetching characters!"))
      }
    }

    fetchCharacters()
  }, [host])

  useEffect(() => {
    if (isCastRole) return
    setValue("character", null, {
      shouldDirty: true,
      shouldValidate: false,
    })
  }, [isCastRole, setValue])

  useEffect(() => {
    if (!isCastRole || !selectedCharacter) return

    const selectedCharacterData = characters.find(
      (character) => String(character.value) === String(selectedCharacter)
    )

    if (!selectedCharacterData) return

    setValue("callTimeUserName", selectedCharacterData.callTimeUserName, {
      shouldDirty: true,
      shouldValidate: false,
    })
  }, [characters, isCastRole, selectedCharacter, setValue])

  useEffect(() => {
    const user = initialData?.initialData?.original

    if (!user || !isCastRole || !characters.length) return

    const matchingCharacter = characters.find(
      (character) =>
        String(character.callTimeUserName || "").toLowerCase() ===
        String(user.call_time_username || "").toLowerCase()
    )

    setValue("character", matchingCharacter?.value || null, {
      shouldDirty: false,
      shouldValidate: false,
    })
  }, [characters, initialData, isCastRole, setValue])

  const onSubmit = async (data) => {
    const {
      callTimeUserName,
      character,
      email,
      firstName,
      lastName,
      groups,
      phoneNumber,
      username,
    } = data

    const castCharacterId =
      isCastRole && character !== null && character !== undefined && character !== ""
        ? Number(character)
        : null

    const body = {
      username,
      first_name: firstName,
      last_name: lastName,
      email,
      groups: [groups],
      call_time_username: callTimeUserName || "",
      phone_number: phoneNumber,
      ...(castCharacterId !== null ? { cast_character: castCharacterId } : {}),
    }

    try {
      const response = await axios.put(
        `/${localStorage.getItem("projectSlug")}/users/${
          initialData?.initialData?.original?.id
        }/`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          withCredentials: true,
        }
      )
      if (response?.status !== 200) {
        throw response?.error || new Error("Failed to edit user")
      }
      toast.success("User Edited Successfully!")
      reset()
      initialData?.handleCloseEditUserModal()
      initialData?.fetchUsers()
    } catch (error) {
      console.log("Failed to edit user: ", error)
      toast.error(getErrorMessage(error, "Failed to edit user!"))
    }
  }

  return (
    <ManModal
      opened={initialData?.userEditFormOpened}
      onClose={initialData?.handleCloseEditUserModal}
      title="Edit User"
      centered
    >
      <ManBox maw={400} mx="auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <MuiTextInput
            disabled={true}
            name="email"
            withAsterisk
            label="Email"
            placeholder="Enter email"
            {...register("email")}
          />
          {errors.email && <ErrorText message={errors.email?.message} />}

          <MuiTextInput
            name="username"
            withAsterisk
            label="Username"
            placeholder="Enter username"
            {...register("username")}
            disabled={true}
          />
          {errors.username && <ErrorText message={errors.username?.message} />}

          <MuiTextInput
            name="firstName"
            withAsterisk
            label="First Name"
            placeholder="Enter First Name"
            {...register("firstName")}
          />
          {errors.firstName && <ErrorText message={errors.firstName?.message} />}

          <MuiTextInput
            name="lastName"
            withAsterisk
            label="Last Name"
            placeholder="Enter Last Name"
            {...register("lastName")}
          />
          {errors.lastName && <ErrorText message={errors.lastName?.message} />}

          <MuiTextInput
            name="phoneNumber"
            withAsterisk
            label="Phone Number"
            placeholder="Enter Phone Number"
            {...register("phoneNumber")}
          />
          {errors.phoneNumber && (
            <ErrorText message={errors.phoneNumber?.message} />
          )}

          <Controller
            name="groups"
            control={control}
            render={({ field }) => (
              <ManSelect
                data={allGroups}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                label="Role"
                withAsterisk
                // error={!!errors.grpups}
                // errorLabel={errors.groups && errors.groups.message}
                placeholder="Please select a role"
              />
            )}
          />

          {errors.groups && <ErrorText message={errors.groups?.message} />}

          {isCastRole && (
            <Controller
              name="character"
              control={control}
              render={({ field }) => (
                <ManSelect
                  data={characterOptions}
                  value={field.value}
                  onChange={(value) => field.onChange(value === "" ? null : value)}
                  label="Character"
                  placeholder="Please select a character"
                  searchable
                  nothingFound="No characters found"
                />
              )}
            />
          )}

          {!hideCallTimeUserName && (
            <>
              {isCrewRole ? (
                <Controller
                  name="callTimeUserName"
                  control={control}
                  render={({ field }) => (
                    <ManSelect
                      data={callnameOptions}
                      value={field.value ?? null}
                      onChange={(value) => field.onChange(value)}
                      label="Callname"
                      withAsterisk
                      placeholder="Select callname"
                      searchable
                      clearable={false}
                      allowDeselect={false}
                      withinPortal={true}
                      maxDropdownHeight={220}
                      transitionProps={{ duration: 0 }}
                      nothingFound="No callnames found"
                    />
                  )}
                />
              ) : (
                <MuiTextInput
                  name="callTimeUserName"
                  withAsterisk
                  label="Callname"
                  placeholder="Enter Callname"
                  readOnly={isCastRole && !!selectedCharacter}
                  {...register("callTimeUserName")}
                />
              )}
              {errors.callTimeUserName && (
                <ErrorText message={errors.callTimeUserName?.message} />
              )}
            </>
          )}

          <ManGroup position="right" mt="md">
            <ManButton disabled={isSubmitting} type="submit">
              Submit
            </ManButton>
          </ManGroup>
        </form>
      </ManBox>
    </ManModal>
  )
}
export default EditUserModal
